import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Badge,
  Alert,
  Input,
  InputGroup,
  InputGroupText,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import DataTable from '../../components/DataTable';
import ConfirmModal from '../../components/ConfirmModal';
import Loading from '../../components/Loading';
import ProductoForm from './ProductoForm';
import SnowFallComponent from 'components/SnowFallComponent';
import { productosService } from '../../services/productosService';
import { empresasService } from '../../services/empresasService';
import { categoriasService } from '../../services/categoriasService';
import { sucursalesService } from '../../services/sucursalesService';
import { inventarioService } from '../../services/inventarioService';

function Productos() {
  const [productos, setProductos] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSucursales, setLoadingSucursales] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProducto, setEditingProducto] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formKey, setFormKey] = useState(0); // Key para forzar remontaje del formulario
  const [productoToDelete, setProductoToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [filtroSucursal, setFiltroSucursal] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [buscarTexto, setBuscarTexto] = useState('');
  const [showPreciosModal, setShowPreciosModal] = useState(false);
  const [productoPrecios, setProductoPrecios] = useState(null);
  const [preciosSucursal, setPreciosSucursal] = useState({});
  const [savingPrecios, setSavingPrecios] = useState(false);

  useEffect(() => {
    loadEmpresaYSucursales();
  }, []);

  useEffect(() => {
    if (!loadingSucursales) {
      loadProductos();
    }
  }, [filtroSucursal, filtroCategoria, buscarTexto, loadingSucursales]);

  // Verificar si hay un ID en sessionStorage para abrir el detalle/edición automáticamente
  useEffect(() => {
    const openDetailId = sessionStorage.getItem('openDetail_producto');
    if (openDetailId && !loading && empresa) {
      // Cargar el producto específico desde la API para asegurar que esté disponible
      // independientemente del filtro de sucursal
      const loadProductoFromAPI = async () => {
        try {
          const response = await productosService.getById(openDetailId);
          if (response.success && response.data) {
            sessionStorage.removeItem('openDetail_producto');
            const productoData = response.data;
            // Normalizar el producto para que tenga el mismo formato que los productos de la lista
            const productoNormalizado = {
              ProductoID: productoData.ProductoID || productoData.productoID,
              EmpresaID: productoData.EmpresaID || productoData.empresaID,
              SucursalID: productoData.SucursalID || productoData.sucursalID,
              SKU: productoData.SKU || productoData.sku || '',
              Nombre: productoData.Nombre || productoData.nombre || '',
              CodigoBarras: productoData.CodigoBarras || productoData.codigoBarras,
              CategoriaID: productoData.CategoriaID || productoData.categoriaID,
              CategoriaNombre: productoData.CategoriaNombre || productoData.categoriaNombre,
              UnidadMedidaID: productoData.UnidadMedidaID || productoData.unidadMedidaID,
              UnidadMedidaNombre: productoData.UnidadMedidaNombre || productoData.unidadMedidaNombre,
              CostoCompra: productoData.CostoCompra || productoData.costoCompra || 0,
              PrecioVentaGlobal: productoData.PrecioVentaGlobal || productoData.precioVentaGlobal || 0,
              Activo: productoData.Activo !== undefined ? productoData.Activo : (productoData.activo !== undefined ? productoData.activo : true),
              StockActual: null,
            };
            handleEdit(productoNormalizado);
          } else {
            // Si no se encuentra en la API, intentar buscarlo en la lista actual
            sessionStorage.removeItem('openDetail_producto');
            const producto = productos.find(p => 
              (p.ProductoID || p.productoID)?.toString() === openDetailId
            );
            if (producto) {
              handleEdit(producto);
            }
          }
        } catch (error) {
          // Si hay error, intentar buscarlo en la lista actual como fallback
          sessionStorage.removeItem('openDetail_producto');
          const producto = productos.find(p => 
            (p.ProductoID || p.productoID)?.toString() === openDetailId
          );
          if (producto) {
            handleEdit(producto);
          }
        }
      };
      
      loadProductoFromAPI();
    }
  }, [loading, productos, empresa]);

  useEffect(() => {
    if (empresa) {
      loadCategorias();
    } else {
      setCategorias([]);
    }
  }, [empresa]);

  const loadEmpresaYSucursales = async () => {
    try {
      setLoadingSucursales(true);
      // Obtener la empresa activa (solo hay una)
      const empresasResponse = await empresasService.getAll();
      if (empresasResponse.success && empresasResponse.data) {
        const empresasData = Array.isArray(empresasResponse.data) ? empresasResponse.data : [];
        const empresaActiva = empresasData.find(emp => emp.Activo !== false && emp.activo !== false) || empresasData[0];
        if (empresaActiva) {
          setEmpresa(empresaActiva);
          // Cargar sucursales de la empresa
          const sucursalesResponse = await sucursalesService.getAll(empresaActiva.EmpresaID || empresaActiva.empresaID);
          if (sucursalesResponse.success && sucursalesResponse.data) {
            const sucursalesData = Array.isArray(sucursalesResponse.data) ? sucursalesResponse.data : [];
            const normalized = sucursalesData.map(suc => ({
              SucursalID: suc.SucursalID || suc.sucursalID,
              Nombre: suc.Nombre || suc.nombre || '',
            }));
            setSucursales(normalized);
            // Seleccionar la primera sucursal por defecto si hay sucursales
            if (normalized.length > 0) {
              setFiltroSucursal(normalized[0].SucursalID.toString());
            }
          }
        }
      }
    } catch (err) {
      setError('Error al cargar datos de la empresa');
    } finally {
      setLoadingSucursales(false);
    }
  };

  const loadCategorias = async () => {
    try {
      if (!empresa) return;
      const empresaId = empresa.EmpresaID || empresa.empresaID;
      
      const response = await categoriasService.getAll(empresaId);
      if (response.success && response.data) {
        const categoriasData = Array.isArray(response.data) ? response.data : [];
        const normalized = categoriasData.map(cat => ({
          CategoriaID: cat.CategoriaID || cat.categoriaID,
          Nombre: cat.Nombre || cat.nombre || '',
        }));
        setCategorias(normalized);
      }
    } catch (err) {
    }
  };


  const loadProductos = async () => {
    try {
      setLoading(true);
      setError('');
      const empresaId = empresa ? (empresa.EmpresaID || empresa.empresaID) : null;
      const sucursalId = filtroSucursal ? parseInt(filtroSucursal) : null;
      const categoriaId = filtroCategoria ? parseInt(filtroCategoria) : null;
      const buscar = buscarTexto.trim() || null;
      const response = await productosService.getAll(empresaId, sucursalId, categoriaId, buscar);
      if (response.success && response.data) {
        const productosData = Array.isArray(response.data) ? response.data : [];
        // Normalizar los datos para manejar tanto camelCase como PascalCase
        let normalizedData = productosData.map(prod => ({
          ProductoID: prod.ProductoID || prod.productoID,
          EmpresaID: prod.EmpresaID || prod.empresaID,
          SucursalID: prod.SucursalID || prod.sucursalID,
          SKU: prod.SKU || prod.sku || '',
          Nombre: prod.Nombre || prod.nombre || '',
          CodigoBarras: prod.CodigoBarras || prod.codigoBarras,
          CategoriaID: prod.CategoriaID || prod.categoriaID,
          CategoriaNombre: prod.CategoriaNombre || prod.categoriaNombre,
          UnidadMedidaID: prod.UnidadMedidaID || prod.unidadMedidaID,
          UnidadMedidaNombre: prod.UnidadMedidaNombre || prod.unidadMedidaNombre,
          CostoCompra: prod.CostoCompra || prod.costoCompra || 0,
          PrecioVentaGlobal: prod.PrecioVentaGlobal || prod.precioVentaGlobal || 0,
          Activo: prod.Activo !== undefined ? prod.Activo : (prod.activo !== undefined ? prod.activo : true),
          StockActual: null, // Se cargará después si hay sucursal seleccionada
        }));

        // Si hay una sucursal seleccionada, obtener el stock de cada producto
        if (sucursalId && normalizedData.length > 0) {
          const stockPromises = normalizedData.map(async (prod) => {
            try {
              const stockResponse = await inventarioService.getStock(prod.ProductoID, sucursalId);
              if (stockResponse.success && stockResponse.data) {
                const stockData = stockResponse.data;
                const stockActual = stockData.StockActual !== undefined 
                  ? stockData.StockActual 
                  : (stockData.stockActual !== undefined ? stockData.stockActual : 0);
                return { ...prod, StockActual: parseFloat(stockActual) || 0 };
              }
              return { ...prod, StockActual: 0 };
            } catch (err) {
              return { ...prod, StockActual: 0 };
            }
          });
          normalizedData = await Promise.all(stockPromises);
        }

        setProductos(normalizedData);
      } else {
        setError(response.Message || response.message || 'Error al cargar productos');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingProducto(null);
    setFormKey(prev => prev + 1); // Incrementar key para forzar remontaje del formulario
    setShowForm(true);
  };

  const handleEdit = (producto) => {
    setEditingProducto(producto);
    setShowForm(true);
  };

  const handleDelete = (producto) => {
    setProductoToDelete(producto);
    setShowDeleteModal(true);
  };

  const handlePrecios = async (producto) => {
    setProductoPrecios(producto);
    // Inicializar precios con el precio global por defecto
    const precios = {};
    sucursales.forEach(suc => {
      precios[suc.SucursalID] = producto.PrecioVentaGlobal || producto.precioVentaGlobal || 0;
    });
    setPreciosSucursal(precios);
    setShowPreciosModal(true);
  };

  const savePrecios = async () => {
    if (!productoPrecios) return;

    try {
      setSavingPrecios(true);
      const productoId = productoPrecios.ProductoID || productoPrecios.productoID;
      
      // Guardar precios para cada sucursal
      const promises = Object.entries(preciosSucursal).map(([sucursalId, precio]) => {
        return productosService.updatePrecioSucursal(
          productoId,
          parseInt(sucursalId),
          parseFloat(precio)
        );
      });

      await Promise.all(promises);
      setSuccess('Precios actualizados exitosamente');
      setShowPreciosModal(false);
      setProductoPrecios(null);
      setPreciosSucursal({});
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Error al guardar precios');
    } finally {
      setSavingPrecios(false);
    }
  };

  const confirmDelete = async () => {
    if (!productoToDelete) return;

    try {
      setDeleting(true);
      const productoId = productoToDelete.ProductoID || productoToDelete.productoID;
      const response = await productosService.delete(productoId);
      if (response.success) {
        setSuccess('Producto desactivado exitosamente');
        setShowDeleteModal(false);
        setProductoToDelete(null);
        loadProductos();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.Message || response.message || 'Error al desactivar producto');
      }
    } catch (err) {
      setError(err.message || 'Error de conexión al desactivar producto');
    } finally {
      setDeleting(false);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingProducto(null);
    setSuccess(editingProducto ? 'Producto actualizado exitosamente' : 'Producto creado exitosamente');
    loadProductos();
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingProducto(null);
    setFormKey(prev => prev + 1); // Incrementar key para resetear el formulario la próxima vez
  };

  const getSucursalNombre = (sucursalId) => {
    if (!sucursalId) return 'Todas las sucursales';
    const sucursal = sucursales.find(s => (s.SucursalID || s.sucursalID) === sucursalId);
    return sucursal ? (sucursal.Nombre || sucursal.nombre) : '-';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(value || 0);
  };

  const columns = [
    {
      key: 'sucursal',
      label: 'Sucursal',
      accessor: 'SucursalID',
      render: (value) => getSucursalNombre(value),
    },
    {
      key: 'sku',
      label: 'SKU',
      accessor: 'SKU',
    },
    {
      key: 'nombre',
      label: 'Nombre',
      accessor: 'Nombre',
    },
    {
      key: 'categoria',
      label: 'Categoría',
      accessor: 'CategoriaNombre',
      render: (value) => value || '-',
    },
    {
      key: 'unidadMedida',
      label: 'Unidad',
      accessor: 'UnidadMedidaNombre',
      render: (value) => value || '-',
    },
    {
      key: 'costoCompra',
      label: 'Costo',
      accessor: 'CostoCompra',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'precioVentaGlobal',
      label: 'Precio Global',
      accessor: 'PrecioVentaGlobal',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'stockActual',
      label: 'Stock Actual',
      accessor: 'StockActual',
      render: (value, row) => {
        if (value === null || value === undefined) {
          return filtroSucursal ? '-' : 'Seleccione una sucursal';
        }
        return parseFloat(value).toFixed(2);
      },
    },
    {
      key: 'activo',
      label: 'Estado',
      accessor: 'Activo',
      render: (value) => (
        <Badge color={value ? 'success' : 'secondary'}>
          {value ? 'Activo' : 'Inactivo'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, row) => (
        <div>
          <Button
            color="info"
            size="sm"
            className="mr-2"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row);
            }}
            title="Editar"
          >
            <i className="tim-icons icon-pencil" />
          </Button>
          <Button
            color="warning"
            size="sm"
            className="mr-2"
            onClick={(e) => {
              e.stopPropagation();
              handlePrecios(row);
            }}
            title="Precios por Sucursal"
          >
            <i className="tim-icons icon-money-coins" />
          </Button>
          <Button
            color="danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row);
            }}
            title="Desactivar"
          >
            <i className="tim-icons icon-simple-remove" />
          </Button>
        </div>
      ),
    },
  ];

  if (showForm) {
    return (
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">
                  {editingProducto ? 'Editar Producto' : 'Nuevo Producto'}
                </CardTitle>
              </CardHeader>
              <CardBody>
                <ProductoForm
                  key={formKey} // Key para forzar remontaje cuando cambia
                  producto={editingProducto}
                  onSuccess={handleFormSuccess}
                  onCancel={handleFormCancel}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="content">
      <SnowFallComponent />
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <Row>
                <Col>
                  <CardTitle tag="h4">Productos</CardTitle>
                </Col>
                <Col className="text-right">
                  <Button color="primary" onClick={handleCreate}>
                    <i className="tim-icons icon-simple-add" /> Nuevo Producto
                  </Button>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="3">
                  <select
                    className="form-control"
                    value={filtroSucursal}
                    onChange={(e) => setFiltroSucursal(e.target.value)}
                    disabled={loadingSucursales}
                  >
                    <option value="">Todas las sucursales</option>
                    {sucursales.map((suc) => (
                      <option key={suc.SucursalID} value={suc.SucursalID}>
                        {suc.Nombre}
                      </option>
                    ))}
                  </select>
                </Col>
                <Col md="3">
                  <select
                    className="form-control"
                    value={filtroCategoria}
                    onChange={(e) => setFiltroCategoria(e.target.value)}
                    disabled={!empresa}
                  >
                    <option value="">Todas las categorías</option>
                    {categorias.map((cat) => (
                      <option key={cat.CategoriaID} value={cat.CategoriaID}>
                        {cat.Nombre}
                      </option>
                    ))}
                  </select>
                </Col>
                <Col md="6">
                  <InputGroup>
                    <InputGroupText>
                      <i className="tim-icons icon-zoom-split" />
                    </InputGroupText>
                    <Input
                      type="text"
                      placeholder="Buscar por SKU, nombre o código de barras..."
                      value={buscarTexto}
                      onChange={(e) => setBuscarTexto(e.target.value)}
                    />
                  </InputGroup>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert color="success" className="mb-3">
                  {success}
                </Alert>
              )}

              {loading ? (
                <Loading />
              ) : (
                <DataTable columns={columns} data={productos} />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ConfirmModal
        isOpen={showDeleteModal}
        toggle={() => {
          setShowDeleteModal(false);
          setProductoToDelete(null);
        }}
        title="Desactivar Producto"
        message={`¿Está seguro de desactivar el producto "${productoToDelete?.Nombre || productoToDelete?.nombre}"?`}
        confirmText="Desactivar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        loading={deleting}
      />

      <Modal isOpen={showPreciosModal} toggle={() => setShowPreciosModal(false)} size="lg" className="modal-black">
        <ModalHeader toggle={() => setShowPreciosModal(false)}>
          Precios por Sucursal - {productoPrecios?.Nombre || productoPrecios?.nombre}
        </ModalHeader>
        <ModalBody>
          {sucursales.length === 0 ? (
            <Alert color="warning">
              Seleccione una empresa para ver las sucursales disponibles.
            </Alert>
          ) : (
            <div>
              <p className="text-muted mb-3">
                Precio Global: <strong>{formatCurrency(productoPrecios?.PrecioVentaGlobal || productoPrecios?.precioVentaGlobal || 0)}</strong>
              </p>
              {sucursales.map((suc) => (
                <Row key={suc.SucursalID} className="mb-3">
                  <Col md="4">
                    <label>{suc.Nombre}</label>
                  </Col>
                  <Col md="8">
                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={preciosSucursal[suc.SucursalID] || 0}
                      onChange={(e) => {
                        setPreciosSucursal({
                          ...preciosSucursal,
                          [suc.SucursalID]: e.target.value,
                        });
                      }}
                    />
                  </Col>
                </Row>
              ))}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowPreciosModal(false)}>
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={savePrecios}
            disabled={savingPrecios || sucursales.length === 0}
          >
            {savingPrecios ? 'Guardando...' : 'Guardar Precios'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default Productos;

